<template>
  <div class="sw">
    <!-- Floating trigger. Hidden on public pages where nobody is signed in. -->
    <button v-if="visible" class="sw__fab" type="button" aria-label="Contact support" @click="open()">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <span>Support</span>
    </button>

    <Teleport to="body">
      <transition name="sw-fade">
        <div v-if="showModal" class="sw__backdrop" @click.self="close">
          <transition name="sw-rise" appear>
            <div class="sw__modal" role="dialog" aria-modal="true" aria-labelledby="sw-title">
              <button class="sw__close" type="button" aria-label="Close" @click="close">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>

              <!-- Sent -->
              <div v-if="submitted" class="sw__sent">
                <div class="sw__sent-mark" aria-hidden="true">
                  <svg viewBox="0 0 52 52">
                    <circle class="sw__sent-ring" cx="26" cy="26" r="23" />
                    <path class="sw__sent-tick" d="M15 26.5l7.5 7.5L38 19" />
                  </svg>
                </div>
                <h3 id="sw-title" ref="sentRef" tabindex="-1" class="sw__sent-title">Thanks — we have it</h3>
                <p class="sw__sent-body">
                  It's in the support queue and someone on the team will pick it up.
                </p>
                <button v-if="lastId" type="button" class="sw__ref" :class="{ 'is-copied': copied }" @click="copyRef">
                  <span class="sw__ref-label">Reference</span>
                  <span class="sw__ref-value">{{ lastId }}</span>
                  <span class="sw__ref-action">{{ copied ? 'Copied' : 'Copy' }}</span>
                </button>
                <div class="sw__actions sw__actions--center">
                  <button class="sw__btn sw__btn--primary" @click="close">Done</button>
                </div>
              </div>

              <!-- Compose -->
              <form v-else @submit.prevent="submit">
                <header class="sw__header">
                  <p class="sw__eyebrow"><span class="sw__eyebrow-dot" aria-hidden="true"></span>Support</p>
                  <h3 id="sw-title" class="sw__title">Send us a note</h3>
                </header>

                <fieldset class="sw__fieldset">
                  <legend class="sw__legend">What is this?</legend>
                  <div class="sw__kinds">
                    <label
                      v-for="kind in KINDS"
                      :key="kind.id"
                      class="sw__kind"
                      :class="{ 'is-selected': form.category === kind.id }"
                    >
                      <input type="radio" name="sw-kind" :value="kind.id" v-model="form.category" />
                      <span>{{ kind.label }}</span>
                    </label>
                  </div>
                </fieldset>

                <label class="sw__field">
                  <span class="sw__label">{{ activeKind.prompt }}</span>
                  <textarea
                    ref="messageRef"
                    v-model="form.message"
                    rows="5"
                    required
                    maxlength="4000"
                    class="sw__textarea"
                    :placeholder="activeKind.placeholder"
                  />
                </label>

                <!-- Only a defect has a severity, and only a defect benefits
                     from a screenshot of the screen it happened on. -->
                <template v-if="form.category === 'bug'">
                  <label class="sw__field">
                    <span class="sw__label">How badly is it hurting?</span>
                    <select v-model="form.severity" class="sw__select">
                      <option value="low">Low — cosmetic or a minor annoyance</option>
                      <option value="medium">Medium — awkward, but I can work around it</option>
                      <option value="high">High — it blocks my work</option>
                      <option value="critical">Critical — data loss or an outage</option>
                    </select>
                  </label>

                  <label class="sw__check">
                    <input type="checkbox" v-model="form.includeScreenshot" />
                    <span>Attach a screenshot of this page</span>
                  </label>
                </template>

                <p class="sw__note">
                  We attach the page you're on, your browser, and your recent activity automatically.
                </p>

                <transition name="sw-fade">
                  <p v-if="error" class="sw__error" role="alert">{{ error }}</p>
                </transition>

                <div class="sw__actions">
                  <button type="button" class="sw__btn" @click="close">Cancel</button>
                  <button type="submit" class="sw__btn sw__btn--primary" :disabled="sending || !form.message.trim()">
                    <span v-if="sending" class="sw__spinner" aria-hidden="true"></span>
                    {{ sending ? 'Sending…' : 'Send' }}
                  </button>
                </div>
              </form>
            </div>
          </transition>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
/**
 * The floating support widget — the only place inside the product where a
 * person submits something to the team. Everything it sends lands in the
 * Admin → Support inbox.
 *
 * It takes a category because the three things people arrive with need
 * different questions: a bug wants a severity and a screenshot, an idea and a
 * question want neither, and asking for them anyway is how a form starts
 * feeling like paperwork.
 */
import { computed, onMounted, onUnmounted, reactive, ref, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { supportAPI } from '@/services/api'
import { useAuthStore } from '@/stores/authStore.js'
import { getBreadcrumbs } from '@/utils/errorReporter'
import { onOpenSupportWidget } from '@/utils/supportWidgetBus'

const KINDS = [
  {
    id: 'bug',
    label: 'Something broken',
    prompt: 'What went wrong?',
    placeholder: 'What were you trying to do, what did you expect, and what happened instead?',
  },
  {
    id: 'idea',
    label: 'An idea',
    prompt: 'What would you like to see?',
    placeholder: "Tell us the problem you're trying to solve — we'd rather hear that than the feature.",
  },
  {
    id: 'question',
    label: 'A question',
    prompt: 'What do you need to know?',
    placeholder: 'Ask away — access, approvals, points, or anything else about the hub.',
  },
]

const authStore = useAuthStore()
const route = useRoute()

const showModal = ref(false)
const submitted = ref(false)
const sending = ref(false)
const lastId = ref('')
const error = ref('')
const copied = ref(false)
const messageRef = ref(null)
const sentRef = ref(null)
let copiedTimer = null

const form = reactive({
  category: 'bug',
  message: '',
  severity: 'medium',
  includeScreenshot: false,
})

const visible = computed(() => authStore.isAuthenticated)
const activeKind = computed(() => KINDS.find((k) => k.id === form.category) || KINDS[0])

function reset() {
  form.category = 'bug'
  form.message = ''
  form.severity = 'medium'
  form.includeScreenshot = false
  submitted.value = false
  lastId.value = ''
  error.value = ''
  copied.value = false
}

function open(payload = {}) {
  reset()
  if (payload?.prefillMessage) form.message = String(payload.prefillMessage)
  if (payload?.category && KINDS.some((k) => k.id === payload.category)) form.category = payload.category
  showModal.value = true
  nextTick(() => messageRef.value?.focus())
}

function close() {
  showModal.value = false
}

function onKeydown(event) {
  if (event.key === 'Escape' && showModal.value) close()
}

async function copyRef() {
  try {
    await navigator.clipboard.writeText(lastId.value)
    copied.value = true
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => { copied.value = false }, 2000)
  } catch {
    // Clipboard blocked — the reference is on screen either way.
  }
}

async function captureScreenshot() {
  try {
    // html2canvas is an OPTIONAL runtime dependency. We use a variable
    // specifier so bundlers don't require it at build time — if the package
    // isn't installed, screenshots are silently skipped and the report still
    // submits with everything else.
    const specifier = 'html2canvas'
    const mod = await import(/* @vite-ignore */ specifier).catch(() => null)
    if (!mod?.default) return null
    const canvas = await mod.default(document.body, { scale: 0.6, useCORS: true, logging: false })
    return canvas.toDataURL('image/jpeg', 0.6)
  } catch {
    return null
  }
}

async function submit() {
  sending.value = true
  error.value = ''
  try {
    let screenshot = null
    if (form.category === 'bug' && form.includeScreenshot) {
      screenshot = await captureScreenshot()
      if (!screenshot) error.value = 'Screenshot capture is unavailable in this browser — sending without it.'
    }

    const res = await supportAPI.reportBug({
      source: 'user-report',
      category: form.category,
      severity: form.category === 'bug' ? form.severity : 'medium',
      message: form.message,
      page: document.title,
      url: window.location.href,
      userAgent: navigator.userAgent,
      context: { route: route?.fullPath },
      logs: getBreadcrumbs(),
      screenshot,
    })
    lastId.value = res.data?.id?.replace(/^bugreport#/, '').split('#').pop()?.toUpperCase() || ''
    submitted.value = true
    nextTick(() => sentRef.value?.focus())
  } catch (err) {
    error.value = err?.response?.data?.error
      || (err?.response
        ? `The server rejected that (HTTP ${err.response.status}).`
        : 'Could not reach the support service — nothing was sent.')
  } finally {
    sending.value = false
  }
}

let unsubscribe = () => {}
onMounted(() => {
  unsubscribe = onOpenSupportWidget(open)
  window.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  unsubscribe()
  window.removeEventListener('keydown', onKeydown)
  clearTimeout(copiedTimer)
})
</script>

<style scoped>
/* Built on the hub's shared tokens (admin-theme.css) so the widget belongs to
   the same product as everything it floats over. */

.sw__fab {
  position: fixed;
  bottom: 88px;
  right: 24px;
  z-index: 1999;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--ink);
  border-radius: var(--radius-pill);
  background: var(--ink);
  color: #fff;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow: 0 6px 20px -8px rgba(16, 24, 40, 0.45);
  transition: transform 180ms var(--ease), box-shadow 180ms var(--ease), background 180ms var(--ease);
}

.sw__fab svg { width: 15px; height: 15px; }

.sw__fab:hover {
  transform: translateY(-1px);
  background: var(--ink-2);
  box-shadow: 0 10px 26px -8px rgba(16, 24, 40, 0.5);
}

.sw__fab:active { transform: translateY(0); }
.sw__fab:focus-visible { outline: 2px solid var(--accent); outline-offset: 3px; }

.sw__backdrop {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(11, 18, 32, 0.34);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.sw__modal {
  position: relative;
  width: min(460px, 100%);
  max-height: 88vh;
  overflow-y: auto;
  padding: 1.65rem 1.75rem 1.4rem;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
  box-shadow: var(--shadow-lg);
}

.sw__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--muted-2);
  cursor: pointer;
  transition: background 150ms var(--ease), color 150ms var(--ease);
}

.sw__close:hover { background: #eef0f4; color: var(--ink); }

.sw__header { margin-bottom: 1.35rem; padding-right: 32px; }

.sw__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  color: var(--muted);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.sw__eyebrow-dot {
  width: 5px;
  height: 5px;
  border-radius: var(--radius-pill);
  background: var(--accent-2);
  box-shadow: 0 0 0 3px var(--gold-100);
}

.sw__title {
  margin: 0;
  font-size: 1.22rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink);
}

.sw__fieldset { margin: 0 0 1.2rem; padding: 0; border: 0; }

.sw__legend {
  padding: 0;
  margin-bottom: 10px;
  font-size: 0.79rem;
  font-weight: 600;
  color: var(--ink-3);
}

.sw__kinds { display: flex; flex-wrap: wrap; gap: 7px; }

.sw__kind {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 8px 22px 8px 13px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: transparent;
  color: var(--ink-2);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 170ms var(--ease), border-color 170ms var(--ease), color 170ms var(--ease);
}

/* Visually hidden, not hidden — the native radio keeps arrow-key navigation. */
.sw__kind input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  white-space: nowrap;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
}

.sw__kind:hover { border-color: #cbd0da; background: var(--surface-muted); }
.sw__kind:has(input:focus-visible) { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(43, 58, 85, 0.12); }

.sw__kind.is-selected {
  border-color: var(--ink);
  background: var(--ink);
  color: #fff;
}

.sw__kind.is-selected::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 10px;
  width: 4px;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--accent-2);
  animation: sw-dot 260ms var(--ease) both;
}

.sw__field { display: block; margin-bottom: 1.1rem; }

.sw__label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.79rem;
  font-weight: 600;
  color: var(--ink-3);
}

.sw__textarea,
.sw__select {
  width: 100%;
  padding: 11px 13px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--ink);
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.6;
  box-sizing: border-box;
  transition: border-color 160ms var(--ease), box-shadow 160ms var(--ease);
}

.sw__textarea { resize: vertical; min-height: 116px; }
.sw__textarea::placeholder { color: var(--muted-2); }

.sw__select {
  appearance: none;
  padding-right: 36px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'><path fill='none' stroke='%2398a2b3' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 13px center;
  background-size: 11px 7px;
  cursor: pointer;
}

.sw__textarea:hover,
.sw__select:hover { border-color: #cbd0da; }

.sw__textarea:focus,
.sw__select:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(43, 58, 85, 0.10);
}

.sw__check {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 1rem;
  color: var(--ink-3);
  font-size: 0.83rem;
  cursor: pointer;
}

.sw__note {
  margin: 0 0 1.2rem;
  color: var(--muted-2);
  font-size: 0.77rem;
  line-height: 1.55;
}

.sw__error {
  margin: 0 0 1rem;
  color: var(--danger);
  font-size: 0.81rem;
  line-height: 1.5;
}

.sw__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 1.1rem;
  border-top: 1px solid var(--line);
}

.sw__actions--center { justify-content: center; border-top: 0; padding-top: 0.5rem; }

.sw__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--ink-2);
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 170ms var(--ease), border-color 170ms var(--ease), color 170ms var(--ease), opacity 170ms var(--ease);
}

.sw__btn:hover:not(:disabled) { background: var(--surface-muted); border-color: #d5d9e0; color: var(--ink); }
.sw__btn:disabled { opacity: 0.5; cursor: not-allowed; }

.sw__btn--primary {
  border-color: var(--ink);
  background: var(--ink);
  color: #fff;
}

.sw__btn--primary:hover:not(:disabled) { background: var(--ink-2); border-color: var(--ink-2); color: #fff; }

.sw__spinner {
  width: 13px;
  height: 13px;
  border-radius: var(--radius-pill);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  animation: sw-spin 700ms linear infinite;
}

/* ─── Sent ───────────────────────────────────────────────────── */
.sw__sent { text-align: center; padding: 0.75rem 0 0.25rem; }
.sw__sent-mark { width: 54px; height: 54px; margin: 0 auto 1rem; }
.sw__sent-mark svg { width: 100%; height: 100%; }

.sw__sent-ring {
  fill: none;
  stroke: var(--success);
  stroke-width: 2;
  opacity: 0.28;
  stroke-dasharray: 145;
  stroke-dashoffset: 145;
  animation: sw-draw 520ms var(--ease) forwards;
}

.sw__sent-tick {
  fill: none;
  stroke: var(--success);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 38;
  stroke-dashoffset: 38;
  animation: sw-draw 420ms var(--ease) 300ms forwards;
}

.sw__sent-title {
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink);
}

.sw__sent-title:focus, .sw__sent-title:focus-visible { outline: none; }

.sw__sent-body {
  margin: 0 auto 1.2rem;
  max-width: 320px;
  color: var(--muted);
  font-size: 0.87rem;
  line-height: 1.6;
}

.sw__ref {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 1.3rem;
  padding: 7px 7px 7px 14px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-pill);
  background: transparent;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 180ms var(--ease), background 180ms var(--ease);
}

.sw__ref:hover { border-color: #cbd0da; background: var(--surface-muted); }
.sw__ref.is-copied { border-color: #c7e4d6; }

.sw__ref-label {
  color: var(--muted);
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.sw__ref-value {
  color: var(--ink);
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.sw__ref-action {
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  background: var(--surface-muted);
  color: var(--muted);
  font-size: 0.72rem;
  font-weight: 600;
}

.sw__ref.is-copied .sw__ref-action { color: var(--success); }

/* ─── Motion ─────────────────────────────────────────────────── */
.sw-fade-enter-active, .sw-fade-leave-active { transition: opacity 180ms var(--ease); }
.sw-fade-enter-from, .sw-fade-leave-to { opacity: 0; }

.sw-rise-enter-active { transition: opacity 220ms var(--ease), transform 220ms var(--ease); }
.sw-rise-enter-from { opacity: 0; transform: translateY(10px) scale(0.99); }

@keyframes sw-spin { to { transform: rotate(360deg); } }
@keyframes sw-draw { to { stroke-dashoffset: 0; } }
@keyframes sw-dot {
  from { opacity: 0; transform: translateY(-50%) scale(0.3); }
  to { opacity: 1; transform: translateY(-50%) scale(1); }
}

.sw__kind.is-selected::after { transform: translateY(-50%); }

@media (prefers-reduced-motion: reduce) {
  .sw *, .sw__modal, .sw__backdrop {
    animation-duration: 1ms !important;
    transition-duration: 1ms !important;
  }
  .sw__sent-ring, .sw__sent-tick { stroke-dashoffset: 0; }
}

@media (max-width: 600px) {
  .sw__fab { bottom: 80px; right: 16px; }
  .sw__fab span { display: none; }
  .sw__fab { width: 40px; padding: 0; justify-content: center; }
  .sw__modal { padding: 1.4rem 1.25rem 1.2rem; }
}
</style>
